// The shape a new project starts with: named location tiers with a seeded
// tree, plus packages. Both clients (mobile and web) create projects through
// this, so the API's state store sees one consistent shape.
import { uid } from './uid';

const DEFAULT_DATA_TYPES = ['Drawings', 'Specifications', 'Instructions', 'Contract', 'Images', 'Reports'];
const DEFAULT_META_TAGS = ['Record', 'Evidence', 'Issue', 'Change', 'Progress', 'Risk'];

// Default tier structure a new project starts with - an admin can add more
// tiers later (see LocationStructureScreen), but every project needs at
// least one to have a working Upload Location picker. Matches the backend's
// shared/locationTiers.js defaultTierDefinitions() so a freshly created
// project's master tree is already in the canonical shape (tierId/children)
// instead of relying on server-side migration.
export function defaultTierDefinitions() {
  return [
    { id: 'tier-1', name: 'Block', order: 0 },
    { id: 'tier-2', name: 'Level', order: 1 },
  ];
}

// A location node is only a place to file documents against - no dates, no
// milestones. Programme information reaches the AI by uploading a programme
// document and running an agent over it.
function defaultLocationNode(tierId, name = '') {
  return { id: uid(), tierId, name, comments: [], uploads: [], children: [] };
}

function defaultMasterRow() {
  const tiers = defaultTierDefinitions();
  return {
    ...defaultLocationNode(tiers[0].id, 'Block A'),
    children: [defaultLocationNode(tiers[1].id, 'Level 1')],
  };
}

// A package is who does the work - a separate axis alongside the location
// tiers, not a tier itself.
function defaultResponsibility() {
  return { id: uid(), name: '', supplier: '', supplierCode: '' };
}

// An app project built from a Procore one. The default shape already carries
// the procore* fields - they were always meant to be filled from here - so
// this maps them across and marks the source, leaving the tier tree, data
// types and tags exactly as a hand-made project gets them.
//
// procoreProjectId is the field that matters: it makes a re-import idempotent
// and tells a push back to Procore where this project came from.
export function buildProjectFromProcore(procoreProject = {}) {
  return {
    ...buildDefaultProject(procoreProject.name || 'Untitled Project', procoreProject.code || ''),
    source: 'procore',
    procoreProjectId: String(procoreProject.externalId || ''),
    // Whose Procore this came from. A workspace can hold projects from several
    // Procore companies - a subcontractor invited into each main contractor's
    // account - and Procore's OAuth is user-scoped, so one connection reaches
    // all of them. Without this the app would pin one company for the whole
    // workspace and send every document to the wrong place but the first.
    procoreCompanyId: String(procoreProject.companyId || ''),
    procoreStatus: procoreProject.status || '',
    procoreStage: procoreProject.stage || '',
    procoreCity: procoreProject.city || '',
    procoreStateRegion: procoreProject.stateRegion || '',
    procoreCountry: procoreProject.country || '',
    procoreStartDate: procoreProject.startDate || '',
    procoreEndDate: procoreProject.endDate || '',
  };
}

export function buildDefaultProject(name = 'New Project', projectNumber = '') {
  return {
    id: uid(),
    name,
    projectNumber: String(projectNumber || '').trim(),
    source: 'manual',
    procoreProjectId: '',
    procoreCompanyCode: '',
    procoreStatus: '',
    procoreStage: '',
    procoreCity: '',
    procoreStateRegion: '',
    procoreCountry: '',
    procoreStartDate: '',
    procoreEndDate: '',
    client: '',
    address: '',
    contactDetails: '',
    website: '',
    taxInfo: '',
    homeComments: [],
    dataTypes: [...DEFAULT_DATA_TYPES],
    tags: [...DEFAULT_META_TAGS],
    tierDefinitions: defaultTierDefinitions(),
    master: [defaultMasterRow()],
    responsibilities: [defaultResponsibility()],
    snapshots: [],
    status: 'active',
  };
}
